<template>
  <div class="english-profile-ai-card">
    <!-- AI生成トリガー(vue-js-modalを開く) -->
    <el-button class="ai-trigger-btn" @click="openDialog" :disabled="isGenerating">
      <span class="sparkle">✨</span> AIに英文Profileを作ってもらう
    </el-button>

    <!-- 入力モーダル -->
    <modal name="english-profile-ai-dialog" :adaptive="true" :scrollable="true" width="92%" height="auto"
      :clickToClose="false" class="english-profile-ai-modal">
      <div class="modal-inner">
        <div class="modal-header">
          <h3>✨ 英文Profile を AIで作成</h3>
          <button class="close-btn" @click="closeDialog">×</button>
        </div>
        <p class="modal-lead">キャリアの基本情報を入力すると、英文レジュメに最適化された Profile セクションを生成します</p>

        <!-- 入力フォーム -->
        <div v-if="!generatedText" class="modal-body">
          <div class="edit-form">
            <div class="edit-form-item">
              <div class="item-name">あなたの強み・コアスキル <span class="required">必須</span></div>
              <el-input type="textarea" :autosize="{ minRows: 3, maxRows: 4 }" v-model="form.strength"
                placeholder="例: クラウドインフラ設計、チームマネジメント、データ分析" maxlength="500"></el-input>
            </div>

            <div class="edit-form-item">
              <div class="item-name">経験年数 <span class="required">必須</span></div>
              <div class="inline-input">
                <el-input v-model.number="form.experienceYears" placeholder="例: 8" class="input-xs"></el-input>
                <span class="unit">年</span>
              </div>
            </div>

            <div class="edit-form-item">
              <div class="item-name">希望業界 <span class="optional">任意</span></div>
              <el-input v-model="form.targetIndustry" placeholder="例: SaaS、フィンテック、ヘルスケア" maxlength="200"></el-input>
            </div>

            <div class="edit-form-item">
              <div class="item-name">希望職種 <span class="optional">任意</span></div>
              <el-input v-model="form.targetPosition" placeholder="例: シニアバックエンドエンジニア、プロダクトマネージャー"
                maxlength="200"></el-input>
            </div>

            <div class="edit-form-item">
              <div class="item-name">具体的な実績 <span class="optional">任意</span></div>
              <el-input type="textarea" :autosize="{ minRows: 2, maxRows: 5 }" v-model="form.achievements"
                placeholder="例: サーバーコストを30%削減、12人のチームをリード、月間500万トランザクション処理基盤を設計" maxlength="1000"></el-input>
            </div>
          </div>

          <div class="action-area">
            <el-button class="generate-btn" @click="generateProfile" :loading="isGenerating" :disabled="!canGenerate">
              <span v-if="!isGenerating">✨ 生成する</span>
              <span v-else>🌀 AIが Profile を生成中...</span>
            </el-button>
            <p v-if="!canGenerate" class="hint">「強み」と「経験年数」は入力必須です</p>
          </div>

          <div v-if="errorMessage" class="error-message">⚠️ {{ errorMessage }}</div>
        </div>

        <!-- 生成結果 -->
        <div v-else class="modal-body result-area">
          <h4>生成された Profile</h4>
          <div class="generated-preview">{{ generatedText }}</div>
          <div v-if="!isPrime && truncated" class="prime-cta">
            <p>🔒 続きを見るには <strong>ヤギプライム</strong> 会員登録が必要です</p>
            <p class="small">無料プランは1日1回・先頭100字までです</p>
            <el-button class="prime-btn" @click="openYagiprimeDialog">⭐ ヤギプライムを見る</el-button>
          </div>
          <div class="action-area">
            <el-button class="adopt-btn" @click="adoptProfile">✓ この Profile を使う</el-button>
            <el-button @click="regenerate" :disabled="isGenerating">↻ 別案を生成</el-button>
            <el-button type="text" @click="resetDialog">入力に戻る</el-button>
          </div>
          <p v-if="usageCount !== null" class="usage-info">本日の使用回数: {{ usageCount }} / {{ maxUsageCount }} 回</p>
        </div>
      </div>
    </modal>

    <!-- 上書き確認モーダル -->
    <modal name="english-profile-replace-confirm" :adaptive="true" width="400px" height="auto"
      class="english-profile-replace-modal">
      <div class="modal-inner">
        <div class="modal-header">
          <h3>既存の文章を置き換えますか？</h3>
        </div>
        <div class="modal-body">
          <p>現在の Profile 欄に入力されている内容が、AIが生成した文章で<strong>上書き</strong>されます。</p>
          <div class="action-area">
            <el-button @click="hideReplaceConfirm">キャンセル</el-button>
            <el-button class="adopt-btn" @click="confirmReplace">上書きする</el-button>
            <el-button @click="appendInsteadOfReplace">末尾に追記</el-button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
export default {
  name: 'EnglishProfileAiCard',
  props: {
    currentValue: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      isGenerating: false,
      form: {
        strength: '',
        experienceYears: '',
        targetIndustry: '',
        targetPosition: '',
        achievements: ''
      },
      generatedText: '',
      truncated: false,
      isPrime: false,
      usageCount: null,
      maxUsageCount: null,
      errorMessage: ''
    }
  },
  computed: {
    canGenerate: function () {
      return !!this.form.strength && this.form.experienceYears !== '' && Number(this.form.experienceYears) >= 0
    }
  },
  methods: {
    openDialog: function () {
      this.errorMessage = ''
      this.$modal.show('english-profile-ai-dialog')
    },
    closeDialog: function () {
      this.$modal.hide('english-profile-ai-dialog')
    },
    resetDialog: function () {
      this.generatedText = ''
      this.truncated = false
      this.errorMessage = ''
    },
    generateProfile: function () {
      if (!this.canGenerate) return
      const app = this

      // ヤギプラ課金サービスに任せる(未ログイン→ログイン誘導、未ヤギプラ→課金ダイアログ)
      app.$yagiprimePaymentService.useFeature(app, 'english-profile-ai', {
        onSuccess: function () {
          app.executeGenerate()
        },
        onAlreadyPremium: function () {
          app.executeGenerate()
        }
      })
    },
    executeGenerate: function () {
      const app = this
      app.isGenerating = true
      app.errorMessage = ''

      const formData = new FormData()
      formData.append('uuid', app.$firebase.user ? app.$firebase.user.uid : '')
      formData.append('strength', app.form.strength)
      formData.append('experience_years', String(app.form.experienceYears))
      formData.append('target_industry', app.form.targetIndustry || '')
      formData.append('target_position', app.form.targetPosition || '')
      formData.append('achievements', app.form.achievements || '')

      app.$axios.post(app.$varuna.endPoints.aiEnglishProfile, formData)
        .then(function (res) {
          const data = res.data
          if (data && data.success) {
            app.generatedText = data.message
            app.isPrime = !!data.is_prime
            app.usageCount = data.usage_count
            app.maxUsageCount = data.max_usage_count
            app.truncated = !app.isPrime && app.generatedText.length >= 95
          } else {
            app.errorMessage = (data && data.message) || 'AI生成に失敗しました'
          }
        })
        .catch(function (err) {
          const status = err.response && err.response.status
          const msg = err.response && err.response.data && err.response.data.message
          if (status === 404) {
            app.errorMessage = msg || 'AIを利用するには会員登録が必要です'
          } else if (status === 400) {
            app.errorMessage = msg || 'AIの使用回数が上限に達しました'
          } else {
            app.errorMessage = msg || 'AI生成中にエラーが発生しました'
          }
        })
        .finally(function () {
          app.isGenerating = false
        })
    },
    regenerate: function () {
      this.generatedText = ''
      this.generateProfile()
    },
    adoptProfile: function () {
      if (this.currentValue && this.currentValue.trim()) {
        this.$modal.show('english-profile-replace-confirm')
        return
      }
      this.$emit('adopt-profile', this.generatedText)
      this.closeDialog()
      this.resetDialog()
    },
    hideReplaceConfirm: function () {
      this.$modal.hide('english-profile-replace-confirm')
    },
    confirmReplace: function () {
      this.$emit('adopt-profile', this.generatedText)
      this.hideReplaceConfirm()
      this.closeDialog()
      this.resetDialog()
    },
    appendInsteadOfReplace: function () {
      const merged = (this.currentValue || '').trim() + '\n\n' + this.generatedText
      this.$emit('adopt-profile', merged)
      this.hideReplaceConfirm()
      this.closeDialog()
      this.resetDialog()
    },
    openYagiprimeDialog: function () {
      this.$eventHub.$emit('open-yagiprime-payment-dialog')
    }
  }
}
</script>

<style scoped lang="scss">
.english-profile-ai-card {
  display: flex;
  justify-content: end;
  margin-bottom: 16px;
}

.ai-trigger-btn {
  background-color: #e3165c;
  border-color: #e3165c;
  color: #fff;
  font-weight: 700;
  padding: 12px;

  &:hover,
  &:focus {
    background-color: #c9135a;
    border-color: #c9135a;
    color: #fff;
  }

  &.is-disabled {
    background-color: #f4a4c3;
    border-color: #f4a4c3;
    color: #fff;
  }

  .sparkle {
    margin-right: 4px;
  }
}

.modal-inner {
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #222;
  }

  .close-btn {
    background: none;
    border: 0;
    font-size: 1.6rem;
    color: #999;
    cursor: pointer;
    line-height: 1;
    padding: 0 4px;
  }
}

.modal-lead {
  color: #666;
  font-size: 0.85rem;
  margin: 0 0 16px;
}

.modal-body {
  .edit-form-item {
    margin-bottom: 14px;

    .item-name {
      font-weight: 600;
      margin-bottom: 6px;
      font-size: 12px;

      .required {
        color: #e3165c;
        font-size: 0.7rem;
        margin-left: 4px;
        font-weight: normal;
      }

      .optional {
        color: #999;
        font-size: 0.7rem;
        margin-left: 4px;
        font-weight: normal;
      }
    }

    .inline-input {
      display: flex;
      align-items: center;

      .input-xs {
        width: 80px;
      }

      .unit {
        margin-left: 6px;
      }
    }
  }
}

.action-area {
  text-align: center;
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;

  .hint {
    color: #999;
    font-size: 0.8rem;
    margin-top: 8px;
    width: 100%;
  }
}

.generate-btn,
.adopt-btn {
  background-color: #e3165c;
  border-color: #e3165c;
  color: #fff;

  &:hover,
  &:focus {
    background-color: #c9135a;
    border-color: #c9135a;
    color: #fff;
  }
}

.prime-btn {
  background-color: #FF8C00;
  border-color: #FF8C00;
  color: #fff;

  &:hover,
  &:focus {
    background-color: #e67a00;
    border-color: #e67a00;
    color: #fff;
  }
}

.error-message {
  background: #fdecea;
  color: #c0392b;
  padding: 10px 14px;
  border-radius: 4px;
  margin-top: 12px;
  font-size: 0.85rem;
}

.result-area {
  h4 {
    margin: 0 0 12px;
    color: #222;
  }

  .generated-preview {
    background: #f6f8fa;
    padding: 14px 16px;
    border-radius: 6px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    color: #222;
  }

  .prime-cta {
    background: #fff8e1;
    border: 1px solid #ffd54f;
    padding: 12px 14px;
    margin: 14px 0;
    border-radius: 6px;

    p {
      margin: 0 0 6px;
    }

    .small {
      font-size: 0.78rem;
      color: #666;
    }
  }

  .usage-info {
    text-align: center;
    color: #999;
    font-size: 0.78rem;
    margin-top: 12px;
  }
}
</style>

<style lang="scss">
@media (min-width: 768px) {
  .v--modal-overlay.english-profile-ai-modal .v--modal-box {
    width: 50% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
}
</style>
